import I18n from "lib/I18n";
import { observable } from "mobx";

interface EntryErrorInterface {
  invalidKeyAllowed: boolean;
  duplicatedKey: boolean;
  emptyKey: boolean;
  invalidKey: boolean;
  notStartsWithAlphabetOrUnderscoreKey: boolean;
  bigqueryReservedKey: boolean;
  emptyType: boolean;
  invalidType: boolean;
  invalidTypeLimit: boolean;
  emptyFields: boolean;
  invalidNestLevel: boolean;
}

export type EntryErrorLevel = "error" | "warning";

export interface EntryErrorMessage {
  message: string;
  level: EntryErrorLevel;
}

export class EntryError {
  @observable accessor invalidKeyAllowed = false;
  @observable accessor duplicatedKey = false;
  @observable accessor emptyKey = false;
  @observable accessor invalidKey = false;
  @observable accessor notStartsWithAlphabetOrUnderscoreKey = false;
  @observable accessor bigqueryReservedKey = false;
  @observable accessor emptyType = false;
  @observable accessor invalidType = false;
  @observable accessor invalidTypeLimit = false;
  @observable accessor emptyFields = false;
  @observable accessor invalidNestLevel = false;
  @observable accessor messages = new Map<string, EntryErrorMessage[]>();

  constructor(props: Partial<EntryErrorInterface>) {
    this.invalidKeyAllowed = props.invalidKeyAllowed ?? false;
    this.duplicatedKey = props.duplicatedKey ?? false;
    this.emptyKey = props.emptyKey ?? false;
    this.invalidKey = props.invalidKey ?? false;
    this.notStartsWithAlphabetOrUnderscoreKey =
      props.notStartsWithAlphabetOrUnderscoreKey ?? false;
    this.bigqueryReservedKey = props.bigqueryReservedKey ?? false;
    this.emptyType = props.emptyType ?? false;
    this.invalidType = props.invalidType ?? false;
    this.invalidTypeLimit = props.invalidTypeLimit ?? false;
    this.emptyFields = props.emptyFields ?? false;
    this.invalidNestLevel = props.invalidNestLevel ?? false;
    this.messages = new Map([
      ["key", this.buildMessagesOnKey()],
      ["type", this.buildErrorMessagesOnType()],
    ]);
  }

  buildMessagesOnKey() {
    const messages: EntryErrorMessage[] = [];
    if (this.duplicatedKey) {
      messages.push({
        message: I18n.t("event_master_editor.errors.duplicated_key"),
        level: "error",
      });
    }

    if (this.emptyKey) {
      messages.push({
        message: I18n.t("event_master_editor.errors.empty_key"),
        level: "error",
      });
    }

    if (this.invalidKey) {
      messages.push({
        message: I18n.t("event_master_editor.errors.invalid_key"),
        level: "error",
      });
    }

    if (this.notStartsWithAlphabetOrUnderscoreKey) {
      messages.push({
        message: I18n.t(
          "event_master_editor.warnings.not_starts_with_alphabet_or_underscore_key",
        ),
        level: "warning",
      });
    }

    if (this.bigqueryReservedKey) {
      messages.push({
        message: I18n.t("event_master_editor.warnings.bigquery_reserved_key"),
        level: "warning",
      });
    }

    return messages;
  }

  buildErrorMessagesOnType() {
    const messages: EntryErrorMessage[] = [];

    if (this.emptyType) {
      messages.push({
        message: I18n.t("event_master_editor.errors.empty_type"),
        level: "error",
      });
    }

    if (this.invalidType) {
      messages.push({
        message: I18n.t("event_master_editor.errors.invalid_type"),
        level: "error",
      });
    }

    if (this.invalidTypeLimit) {
      messages.push({
        message: I18n.t("event_master_editor.errors.invalid_type_limit"),
        level: "error",
      });
    }

    if (this.emptyFields) {
      messages.push({
        message: I18n.t("event_master_editor.errors.empty_fields"),
        level: "error",
      });
    }

    if (this.invalidNestLevel) {
      messages.push({
        message: I18n.t("event_master_editor.errors.invalid_nest_level"),
        level: "error",
      });
    }

    return messages;
  }
}
